import { memo } from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import { Annotation, Device, Zone } from "../../../../../../API";
import ZoneAlertsToggle from "../../../../../../common/components/zones/ZoneAlertsToggle";
import ZoneStatusToggle from "../../../../../../common/components/zones/ZoneStatusToggle";
import { useGetZonesByDeviceTableRow } from "../zonelist/hooks/useGetZonesByDeviceTableRow";
import Skeleton from "@mui/material/Skeleton";
import { Theme } from "@mui/material/styles";
import { S3ImageFullScreen } from "../../../../../../common/components/media/S3ImageFullScreen";
import { useListenToZoneUpdates } from "../../../../../../pages/devices/zones-setup-page/hooks/useListenToZoneChanges";
import { DEFAULT_MEDIA_ICON_STYLES } from "../../../../../../common/variables/common";

type LiveViewZonesListProps = {
  device: Device;
};

type ZoneWithAnnotations = Zone & {
  annotationsData?: Annotation[] | null;
};

const headerRowStyles = {
  display: "grid",
  gridTemplateColumns: "60% 15% 15%",
  alignItems: "center",
  gap: "16px",
  padding: "12px 16px",
  backgroundColor: "otherBackground.main",
  borderBottom: (theme: Theme) => `1px solid ${theme.palette.divider}`,
};

const zoneRowStyles = {
  display: "grid",
  gridTemplateColumns: "60% 15% 15%",
  alignItems: "center",
  gap: "16px",
  padding: "12px 16px",
  borderBottom: (theme: Theme) => `1px solid ${theme.palette.divider}`,
};

type ListSkeletonProps = {
  count?: number;
};

const ListSkeleton = ({ count = 4 }: ListSkeletonProps): JSX.Element => (
  <Box sx={{ p: "1em" }}>
    {Array.from({ length: count }).map((_, index) => (
      <Skeleton key={index} variant="rectangular" sx={{ my: 3 }} />
    ))}
  </Box>
);

const LiveViewZonesListComponent = ({
  device,
}: LiveViewZonesListProps): JSX.Element => {
  const { loading, zones: fetchedZones } = useGetZonesByDeviceTableRow({
    deviceId: device.id,
  });

  useListenToZoneUpdates();

  const zones = fetchedZones as ZoneWithAnnotations[];
  const hasZones = zones.length > 0;

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ marginBottom: "0.5em" }}>
        <Typography
          variant="body2Regular"
          color="text.secondary"
          sx={{ fontWeight: 500, fontSize: "16px" }}
        >
          Zones List
        </Typography>
      </Box>

      <Box
        sx={{
          border: theme => `1px solid ${theme.palette.otherOutlineBorder.main}`,
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Box sx={headerRowStyles}>
          <Typography
            variant="body2Regular"
            color="text.secondary"
            sx={{ fontWeight: 500, fontSize: "16px" }}
          >
            Name
          </Typography>
          <Typography
            variant="body2Regular"
            color="text.secondary"
            sx={{ fontWeight: 500, fontSize: "16px", textAlign: "center" }}
          >
            Status
          </Typography>
          <Typography
            variant="body2Regular"
            color="text.secondary"
            sx={{ fontWeight: 500, fontSize: "16px", textAlign: "center" }}
          >
            Alerts
          </Typography>
        </Box>

        {loading ? (
          <ListSkeleton count={4} />
        ) : hasZones ? (
          zones.map(zone => (
            <Box key={zone.id} sx={zoneRowStyles}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <Box
                  sx={{
                    width: "64px",
                    height: "48px",
                    borderRadius: "4px",
                    overflow: "hidden",
                    cursor: zone.signedUrlKeyFrame ? "pointer" : "default",
                    backgroundColor: "otherBackground.main",
                  }}
                >
                  {zone.signedUrlKeyFrame && (
                    <S3ImageFullScreen
                      s3Key={zone.signedUrlKeyFrame}
                      fixedHeight="48px"
                      sx={{
                        ...DEFAULT_MEDIA_ICON_STYLES,
                        width: "64px",
                        height: "48px",
                      }}
                      mediaStatusSx={{
                        "& .MuiTypography-root": {
                          fontSize: "50%",
                        },
                      }}
                      fixedWidth={64}
                    />
                  )}
                </Box>

                <Typography variant="body2Regular">{zone.name}</Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <ZoneStatusToggle params={zone} />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <ZoneAlertsToggle params={zone} />
              </Box>
            </Box>
          ))
        ) : (
          <Box sx={{ padding: "24px 16px" }}>
            <Typography variant="body2">
              No zones configured for this device.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const LiveViewZonesList = memo(LiveViewZonesListComponent);

LiveViewZonesList.displayName = "LiveViewZonesList";

export default LiveViewZonesList;
